/**
 * Small shared pieces of the supplier invoice screens (batch 8 T12c3, #3033): number formatting of server values, status chips,
 * and the translation of machine codes. Nothing here evaluates a financial rule.
 */

import { useTranslation } from 'react-i18next';
import { Chip } from '@mui/material';
import { formatCents, formatPercent, formatUnitCents } from './money';
import { errorReason, errorStatus, type FactsLine, type Violation } from '@/hooks/useSupplierInvoices';
import { InvoiceAccessError, OwnerChangedError } from './invoiceSession';

export const NS = 'procurementInvoices';

export interface Formatters {
  cents: (value: number) => string;
  unit: (value: number) => string;
  percent: (value: number) => string;
  date: (iso: string) => string;
  dateTime: (iso: string) => string;
}

export function useFormatters(): Formatters {
  const { i18n } = useTranslation(NS);
  const locale = i18n.language || 'fi';
  return {
    cents: (value) => formatCents(value, locale),
    unit: (value) => formatUnitCents(value, locale),
    percent: (value) => formatPercent(value, locale),
    date: (iso) => {
      const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00` : iso);
      return Number.isNaN(date.getTime()) ? iso : new Intl.DateTimeFormat(locale).format(date);
    },
    dateTime: (iso) => {
      const date = new Date(iso);
      return Number.isNaN(date.getTime()) ? iso : new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(date);
    },
  };
}

type Translate = ReturnType<typeof useTranslation>['t'];

/** `prefix.code` when the catalog has it, else `prefix.unknown` (the code itself is shown, never server prose). */
export function useCodeText(): (prefix: string, code: string) => string {
  const { t, i18n } = useTranslation(NS);
  return (prefix, code) => (i18n.exists(`${NS}:${prefix}.${code}`) ? t(`${prefix}.${code}`) : t(`${prefix}.unknown`, { code }));
}

/** How a PO line is named to the user: its number, SKU and name. Never an id. */
export function poLineLabel(line: Pick<FactsLine, 'lineNumber' | 'partSku' | 'partName'> | undefined, t: Translate): string {
  if (!line) return t('poLine.none');
  const name = [line.partSku, line.partName].filter((part): part is string => typeof part === 'string' && part !== '').join(' – ');
  return name === '' ? t('poLine.numbered', { number: line.lineNumber }) : t('poLine.labelled', { number: line.lineNumber, name });
}

const STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  draft: 'default',
  matched: 'success',
  variance: 'warning',
  accepted: 'info',
  rejected: 'error',
};

export function StatusChip({ status, size = 'small' }: { status: string; size?: 'small' | 'medium' }) {
  const text = useCodeText();
  return <Chip size={size} color={STATUS_COLOR[status] ?? 'default'} label={text('status', status)} />;
}

const LINE_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  matched: 'success',
  accepted_variance: 'info',
  quantity_over: 'warning',
  quantity_under: 'warning',
  price_variance: 'warning',
  unreceived: 'error',
  extra_line: 'error',
  credit_pending: 'warning',
};

export function LineStatusChip({ status }: { status: string | null }) {
  const text = useCodeText();
  const { t } = useTranslation(NS);
  if (status === null) return <Chip size="small" variant="outlined" label={t('lineStatus.none')} />;
  return <Chip size="small" color={LINE_COLOR[status] ?? 'default'} label={text('lineStatus', status)} />;
}

export function ViolationList({ violations }: { violations: readonly Violation[] }) {
  const text = useCodeText();
  const { t } = useTranslation(NS);
  return (
    <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
      {violations.map((violation, index) => (
        <li key={`${violation.code}-${String(index)}`}>
          {violation.lineNumber === undefined ? '' : `${t('violationLine', { number: violation.lineNumber })} `}
          {text('violation', violation.code)}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export interface ErrorText {
  /** Translated, user-facing. Never server prose. */
  message: string;
  /** Violations of the matcher the refusal carried, if any. */
  violations: Violation[];
  reason: string | null;
}

/** The user-facing description of an API failure: the server's machine `reason` where it has one, else its status class. */
export function useErrorText(): (error: unknown) => ErrorText {
  const { t, i18n } = useTranslation(NS);
  return (error) => {
    const reason = errorReason(error);
    const status = errorStatus(error);
    const data = (error as { data?: { details?: { violations?: unknown } } } | null)?.data;
    const raw = data?.details?.violations;
    const violations = Array.isArray(raw)
      ? raw.filter((item): item is Violation => typeof item === 'object' && item !== null && typeof (item as Violation).code === 'string')
      : [];
    let message: string;
    if (error instanceof InvoiceAccessError) message = t('errors.noPermission');
    else if (error instanceof OwnerChangedError) message = t('errors.sessionChanged');
    else if (reason !== null && i18n.exists(`${NS}:errors.reason.${reason}`)) message = t(`errors.reason.${reason}`);
    else if (status === 400) message = t('errors.validation');
    else if (status === 401 || status === 403) message = t('errors.forbidden');
    else if (status === 404) message = t('errors.notFound');
    else if (status === 409) message = t('errors.conflict');
    else if (status === 422) message = t('errors.unprocessable');
    else if (status === 423) message = t('errors.locked');
    else if (status !== null && status >= 500) message = t('errors.server');
    else message = t('errors.network');
    return { message, violations, reason };
  };
}

/** Refusals after which the review the user acted on is outdated: a fresh review is needed, nothing is retried. */
export const STALE_REASONS: readonly string[] = ['stale_revision', 'stale_evidence', 'stale_preview'];
export const isStaleRefusal = (error: unknown): boolean => {
  const reason = errorReason(error);
  return reason !== null && STALE_REASONS.includes(reason);
};
